import { create } from "zustand";
import type { WorkoutCompletion } from "@/types";
import { completionService } from "@/services/completionService";
import { useAuthStore } from "@/state/authStore";
import { useProfileStore } from "@/state/profileStore";
import { toast } from "@/components/Toast/useToast";
import {
  type PrDetectionResult,
  detectPrResults,
  buildPrToast,
  buildNearPrToast,
} from "@/lib/powerPrDetector";
import { captureOperationalError } from "@/lib/monitoring/sentry";

// Higher rank = more informative; used to pick the best completion when
// multiple records exist for the same workout slot (e.g. "skipped" then
// "completed" via Wahoo webhook stored under a different workoutId).
export const STATUS_RANK: Record<string, number> = {
  completed: 4,
  partial: 3,
  skipped: 2,
  planned: 1,
};

export function bestOf(
  candidates: WorkoutCompletion[],
): WorkoutCompletion | undefined {
  return candidates.reduce<WorkoutCompletion | undefined>((best, c) => {
    if (!best) return c;
    return (STATUS_RANK[c.status] ?? 0) > (STATUS_RANK[best.status] ?? 0)
      ? c
      : best;
  }, undefined);
}

/** Pick the higher-ranked of two completions (completed > partial > skipped > planned). */
export function pickBestCompletion(
  a: WorkoutCompletion | undefined,
  b: WorkoutCompletion | undefined,
): WorkoutCompletion | undefined {
  if (!a) return b;
  if (!b) return a;
  return (STATUS_RANK[b.status] ?? 0) > (STATUS_RANK[a.status] ?? 0) ? b : a;
}

// ─── PR toast batch ───────────────────────────────────────────────────────────
// Multiple Wahoo rides may sync at once. Collect results for 400ms then fire
// a single merged toast instead of one per ride.
let _prBatchBuffer: PrDetectionResult[] = [];
let _prBatchTimer: ReturnType<typeof setTimeout> | null = null;

function _flushPrBatch() {
  const buffer = _prBatchBuffer;
  _prBatchBuffer = [];
  _prBatchTimer = null;
  if (buffer.length === 0) return;

  const mergedNewPRs: PrDetectionResult["newPRs"] = {};
  const mergedNearPRs: PrDetectionResult["nearPRs"] = {};
  for (const r of buffer) {
    for (const [key, val] of Object.entries(r.newPRs)) {
      if (!mergedNewPRs[key] || val.new > mergedNewPRs[key].new)
        mergedNewPRs[key] = val;
    }
    for (const [key, val] of Object.entries(r.nearPRs)) {
      if (!mergedNearPRs[key] || val.pctAway < mergedNearPRs[key].pctAway)
        mergedNearPRs[key] = val;
    }
  }

  const merged: PrDetectionResult = { newPRs: mergedNewPRs, nearPRs: mergedNearPRs };
  const prMsg = buildPrToast(merged);
  const nearMsg = buildNearPrToast(merged);
  if (prMsg)
    toast.success(prMsg.title, { message: prMsg.message, duration: 7000 });
  else if (nearMsg)
    toast.info(nearMsg.title, { message: nearMsg.message, duration: 6000 });
}

function _queuePrToast(result: PrDetectionResult) {
  _prBatchBuffer.push(result);
  if (_prBatchTimer) clearTimeout(_prBatchTimer);
  _prBatchTimer = setTimeout(_flushPrBatch, 400);
}

function onSaveError(label: string) {
  return (err: unknown) => {
    const detail =
      err instanceof Error
        ? err.message
        : typeof err === "string"
          ? err
          : "Unknown error";
    console.error(`[completionStore] ${label} save failed:`, err);
    captureOperationalError(err, "completion.save", { label });
    toast.error("Workout log wasn't saved", {
      message: detail,
    });
  };
}

type WorkoutCompletionStore = {
  completions: WorkoutCompletion[];

  // Actions
  addCompletion: (completion: WorkoutCompletion) => void;
  getCompletion: (workoutId: string) => WorkoutCompletion | undefined;
  /** Week-scoped lookup: matches workoutId (bare or date-prefixed) within the given week. */
  getCompletionForWeek: (
    workoutId: string,
    weekStartDate: string,
  ) => WorkoutCompletion | undefined;
  /** Date-scoped lookup: finds any completion (including unplanned Wahoo rides) on a given calendar date. */
  getCompletionForDate: (dateStr: string) => WorkoutCompletion | undefined;
  getCompletionsForWeek: (weekStartDate: string) => WorkoutCompletion[];
  clearCompletions: () => void;
  /** Load all completions from Supabase into the store. */
  loadFromDB: (userId: string) => Promise<void>;
};

export const useWorkoutCompletionStore = create<WorkoutCompletionStore>()(
  (set, get) => ({
    completions: [],

    addCompletion: (completion) => {
      // Fuzzy match: bare ID (workout-mon) must also match date-prefixed form
      // (2026-05-19-workout-mon) so manual completions resolve to the same
      // record regardless of which format was stored first.
      // Date-scoped: when the incoming ID is date-prefixed, only match
      // completions on that same date — prevents 2026-05-19-workout-mon from
      // colliding with 2026-05-12-workout-mon (both end with "-workout-mon").
      const datePrefix = /^(\d{4}-\d{2}-\d{2})-/.exec(completion.workoutId)?.[1];
      const bareId = completion.workoutId.replace(/^\d{4}-\d{2}-\d{2}-/, "");
      const idMatches = (c: WorkoutCompletion) => {
        const idMatch =
          c.workoutId === completion.workoutId ||
          c.workoutId === bareId ||
          c.workoutId.endsWith(`-${bareId}`);
        if (!idMatch) return false;
        if (datePrefix) {
          return (
            c.workoutId.startsWith(datePrefix) ||
            c.completedAt === datePrefix ||
            c.completedAt.startsWith(datePrefix)
          );
        }
        return true;
      };

      // Capture before set() so we know if this is a brand-new ride.
      // Toasts only fire for new completions, not re-syncs of existing ones.
      const isNew = !get().completions.some(idMatches);

      // Compute merged before set() so it's available for completionService.save().
      // Merge so Wahoo metrics (powerAvgWatts, heartRateAvg, etc.) from the
      // existing record are not lost when only RPE / notes are being saved.
      // Trust incoming completedAt — the modal now always uses the workoutId
      // date (never new Date()), so the incoming value is the ground truth.
      // Never let an API-sync "completed" overwrite a user's manual "skipped":
      // if the user deliberately skipped and the incoming is not from the
      // Wahoo webhook (which means the ride actually happened), keep "skipped".
      const existing = get().completions.find(idMatches);
      const protectSkip =
        existing?.status === "skipped" && !completion.fromWebhook;
      const merged = existing
        ? {
            ...existing,
            ...completion,
            // Explicitly protect user-entered fields from being clobbered by
            // undefined when a Wahoo re-sync arrives without notes/effort data.
            notes:
              completion.notes !== undefined
                ? completion.notes
                : existing.notes,
            perceivedEffort:
              completion.perceivedEffort !== undefined
                ? completion.perceivedEffort
                : existing.perceivedEffort,
            rpe:
              completion.rpe !== undefined ? completion.rpe : existing.rpe,
            ...(protectSkip ? { status: "skipped" as const } : {}),
          }
        : completion;

      set((state) => ({
        completions: [
          ...state.completions.filter((c) => !idMatches(c)),
          merged,
        ],
      }));

      const uid = useAuthStore.getState().account?.id;
      if (uid)
        void completionService
          .save(uid, merged)
          .catch(onSaveError("addCompletion"));

      // Update power records when this completion has power data.
      // powerNpWatts feeds the 20-min threshold key (Wahoo summary).
      // bestEfforts feeds sprint/vo2max/threshold/endurance keys (from .FIT),
      // which enables deriveWeakestZone() to bias Feeling Lucky toward the
      // rider's weakest power system.
      if (completion.powerNpWatts || completion.bestEfforts || completion.heartRateAvg) {
        // Snapshot records BEFORE calling updatePowerRecords — that function
        // synchronously mutates Zustand state, so reading afterwards would
        // compare new watts against already-updated values (always no PR found).
        const priorRecords =
          useProfileStore.getState().profile?.powerRecords ?? {};
        // Only detect PRs for brand-new Wahoo rides — not re-syncs of existing
        // completions (which would re-fire the toast on every page visit).
        const prResult =
          completion.fromWebhook && isNew
            ? detectPrResults(completion, priorRecords)
            : null;

        useProfileStore.getState().updatePowerRecords(completion);

        if (prResult) {
          _queuePrToast(prResult);
        }
      }
    },

    getCompletion: (workoutId) => {
      // Collect all matching completions (exact + fuzzy) then return the
      // highest-ranked status so a later "completed" via Wahoo beats an
      // earlier "skipped" stored under a different workoutId format.
      const datePrefix = /^(\d{4}-\d{2}-\d{2})-/.exec(workoutId)?.[1];
      const bareId = workoutId.replace(/^\d{4}-\d{2}-\d{2}-/, "");
      return bestOf(
        get().completions.filter((c) => {
          const idMatch =
            c.workoutId === workoutId ||
            c.workoutId === bareId ||
            c.workoutId.endsWith(`-${bareId}`);
          if (!idMatch) return false;
          if (datePrefix) {
            return (
              c.completedAt === datePrefix ||
              c.completedAt.startsWith(datePrefix)
            );
          }
          return true;
        }),
      );
    },

    getCompletionForDate: (dateStr) =>
      bestOf(
        get().completions.filter(
          (c) => c.completedAt === dateStr || c.completedAt.startsWith(dateStr),
        ),
      ),

    getCompletionForWeek: (workoutId, weekStartDate) => {
      const bareId = workoutId.replace(/^\d{4}-\d{2}-\d{2}-/, "");
      const weekStart = new Date(weekStartDate + "T00:00:00");
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return bestOf(
        get().completions.filter((c) => {
          const idMatch =
            c.workoutId === workoutId ||
            c.workoutId === bareId ||
            c.workoutId.endsWith(`-${bareId}`);
          if (!idMatch) return false;
          // Parse as local noon for date-only strings ("2026-04-06") so UTC midnight
          // ("2026-04-06" → 8 PM EDT) doesn't fall before the local week start.
          const completedDate = new Date(
            c.completedAt.length === 10
              ? c.completedAt + "T12:00:00"
              : c.completedAt,
          );
          return completedDate >= weekStart && completedDate < weekEnd;
        }),
      );
    },

    getCompletionsForWeek: (weekStartDate) => {
      return get().completions.filter((c) => {
        const completedDate = new Date(
          c.completedAt.length === 10
            ? c.completedAt + "T12:00:00"
            : c.completedAt,
        );
        const weekStart = new Date(weekStartDate + "T00:00:00");
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return completedDate >= weekStart && completedDate < weekEnd;
      });
    },

    clearCompletions: () => set({ completions: [] }),

    loadFromDB: async (userId) => {
      const completions = await completionService.loadAll(userId);
      if (completions.length > 0) {
        set({ completions });
        // Retroactive catch-up: populate powerRecords from any bestEfforts that
        // were persisted before this session (e.g. from previous .FIT imports).
        // This ensures deriveWeakestZone() always has current data even for
        // completions that were imported before the bridging fix was shipped.
        const withEfforts = completions.filter(
          (c) => c.status === "completed" && c.bestEfforts,
        );
        if (withEfforts.length > 0) {
          const { updatePowerRecords } = useProfileStore.getState();
          withEfforts.forEach(updatePowerRecords);
        }
      }
    },
  }),
);
